import { useState, useEffect } from "react";
import { api, errMsg, fmtMoney, fmtDate } from "../../lib/api";
import { SERVICES, serviceName } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, TrendingUp, Wallet, Zap, CalendarClock, Tag, Trash2, Users, Route, Box, MapPin, X, Plus } from "lucide-react";

const SIZE_LABELS = { single: "Single item", few: "A few items", studio: "Studio / 1BR", "2br": "2 Bedroom", "3br": "3 Bedroom", whole_house: "Whole house" };

export default function PricingEngine() {
  const [draft, setDraft] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [promos, setPromos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [newPromo, setNewPromo] = useState({ code: "", percent_off: "", label: "" });
  const [newPkg, setNewPkg] = useState({ name: "", price: "" });
  const [newArea, setNewArea] = useState("");

  const load = () => {
    api.get("/pricing").then((r) => setDraft(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/payroll/pending").then((r) => setPayouts(r.data)).catch(() => {});
    api.get("/promo-codes").then((r) => setPromos(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setNested = (section, key, val) => setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: val } }));
  const setFlat = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const num = (v, fb) => (Number(v) >= 0 && v !== "" ? Number(v) : fb);
      const body = {
        base: Object.fromEntries(Object.entries(draft.base).map(([k, v]) => [k, num(v, 0)])),
        size_mult: Object.fromEntries(Object.entries(draft.size_mult).map(([k, v]) => [k, Number(v) || 1])),
        included_miles: num(draft.included_miles, 15),
        per_mile_fee: Number(draft.per_mile_fee) >= 0 && draft.per_mile_fee !== "" ? Number(draft.per_mile_fee) : 2,
        electrical_fee: num(draft.electrical_fee, 30),
        packages: (draft.packages || []).map((p) => ({ ...p, price: Number(p.price) || 0 })),
        service_areas: draft.service_areas || [],
        special_item_fee: num(draft.special_item_fee, 75),
        stairs_fee: num(draft.stairs_fee, 40),
        priority_fee: num(draft.priority_fee, 75),
        extra_stop_fee: num(draft.extra_stop_fee, 50),
        deposit_percent: num(draft.deposit_percent, 20),
        referral_percent: num(draft.referral_percent, 10),
        payout_percent: num(draft.payout_percent, 30),
        dispatcher_quote_limit: num(draft.dispatcher_quote_limit, 2000),
        crew_hourly_benchmark: num(draft.crew_hourly_benchmark, 55),
      };
      const { data } = await api.patch("/pricing", body);
      setDraft(data);
      toast.success("Rate card updated — live on the booking page now");
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const runPayouts = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/payroll/run");
      toast.success(data.paid_employees > 0
        ? `Paid ${data.paid_employees} helper(s) — ${fmtMoney(data.total)} total`
        : "Nothing pending to pay out");
      setReviewOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setRunning(false);
  };

  const addPromo = async () => {
    try {
      await api.post("/promo-codes", { code: newPromo.code, percent_off: Number(newPromo.percent_off), label: newPromo.label });
      setNewPromo({ code: "", percent_off: "", label: "" });
      toast.success("Promo code live — customers can use it at checkout");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const togglePromo = async (p) => {
    await api.patch(`/promo-codes/${p.id}`, { active: !p.active });
    load();
  };

  const deletePromo = async (p) => {
    await api.delete(`/promo-codes/${p.id}`);
    toast.success(`${p.code} deleted`);
    load();
  };

  if (!draft)
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const grouped = Object.values((payouts?.pending || []).reduce((acc, e) => {
    acc[e.employee_id] = acc[e.employee_id] || { name: e.employee_name, records: [], total: 0 };
    acc[e.employee_id].records.push(e);
    acc[e.employee_id].total = Math.round((acc[e.employee_id].total + e.amount) * 100) / 100;
    return acc;
  }, {}));

  const FeeRow = ({ label, field, prefix = "+$", fallback }) => (
    <div className="flex items-center justify-between gap-3">
      <span className="text-base">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="text-sm text-muted-foreground">{prefix}</span>
        <Input data-testid={`pricing-${field}`} type="number" className="w-24 h-11 rounded-xl text-base font-semibold"
          value={draft[field] ?? fallback} onChange={(e) => setFlat(field, e.target.value)} />
      </div>
    </div>
  );

  return (
    <div data-testid="ops-pricing-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline-cc">Revenue Engine</p>
          <h1 className="page-title mt-1.5">Pricing & Payouts</h1>
        </div>
        <Button data-testid="pricing-save-button" onClick={save} disabled={saving}
          className="rounded-full h-12 btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-bold px-7 text-base">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Rate Card"}
        </Button>
      </div>

      <div className="rounded-2xl border bg-card p-6 mb-6 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="pricing-benchmark-card">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-[var(--cc-accent)]" />
          <div>
            <p className="text-base font-semibold">San Diego market benchmark</p>
            <p className="text-xs text-muted-foreground">TaskRabbit helpers: $35–55/hr · Dolly-style per-job payouts</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Crew benchmark ($/hr)</Label>
          <Input data-testid="pricing-benchmark-input" type="number" className="w-24 h-11 rounded-xl text-base font-semibold"
            value={draft.crew_hourly_benchmark} onChange={(e) => setFlat("crew_hourly_benchmark", e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Helper payout (% of job)</Label>
          <Input data-testid="pricing-payout-percent-input" type="number" className="w-20 h-11 rounded-xl text-base font-semibold"
            value={draft.payout_percent} onChange={(e) => setFlat("payout_percent", e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Dispatcher quote limit ($)</Label>
          <Input data-testid="pricing-dispatcher-limit-input" type="number" className="w-28 h-11 rounded-xl text-base font-semibold"
            value={draft.dispatcher_quote_limit ?? 2000} onChange={(e) => setFlat("dispatcher_quote_limit", e.target.value)} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border bg-card">
            <div className="px-6 py-4 border-b flex items-center gap-2">
              <Zap className="w-4 h-4 text-[var(--cc-accent)]" />
              <h2 className="font-heading font-bold text-base">Base Rates by Service</h2>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-4">
              {Object.keys(draft.base).map((k) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {(() => { const S = SERVICES.find((s) => s.id === k); return S ? <S.icon className="w-4 h-4 text-muted-foreground shrink-0" /> : null; })()}
                    <span className="text-base truncate">{serviceName(k)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-muted-foreground">$</span>
                    <Input data-testid={`pricing-base-${k}`} type="number" className="w-24 h-11 rounded-xl text-base font-semibold"
                      value={draft.base[k]} onChange={(e) => setNested("base", k, e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div className="rounded-2xl border bg-card" data-testid="distance-pricing-card">
                <div className="px-6 py-4 border-b flex items-center gap-2">
                  <Route className="w-4 h-4 text-[var(--cc-accent)]" />
                  <h2 className="font-heading font-bold text-base">Distance Pricing</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-base">Included miles (no charge)</span>
                    <Input data-testid="pricing-included-miles" type="number" className="w-24 h-11 rounded-xl text-base font-semibold"
                      value={draft.included_miles ?? 15} onChange={(e) => setFlat("included_miles", e.target.value)} />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-base">Per-mile surcharge after</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-muted-foreground">$</span>
                      <Input data-testid="pricing-per-mile" type="number" step="0.25" className="w-24 h-11 rounded-xl text-base font-semibold"
                        value={draft.per_mile_fee ?? 2} onChange={(e) => setFlat("per_mile_fee", e.target.value)} />
                    </div>
                  </div>
                  <FeeRow label="Electrical disconnect" field="electrical_fee" fallback={30} />
                  <p className="text-xs text-muted-foreground leading-relaxed border-t pt-3">
                    Every booking is priced by real driving distance — the first {draft.included_miles ?? 15} miles are included, then ${draft.per_mile_fee ?? 2}/mile shows as its own transparent line item. Flat zone fees are retired so trips never get charged twice.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border bg-card" data-testid="packages-card">
                <div className="px-6 py-4 border-b flex items-center gap-2">
                  <Box className="w-4 h-4 text-[var(--cc-accent)]" />
                  <h2 className="font-heading font-bold text-base">Named Packages</h2>
                </div>
                <div className="p-6 space-y-3">
                  <p className="text-xs text-muted-foreground">Flat override rates for signature runs — used instead of distance pricing when you quote with them.</p>
                  {(draft.packages || []).map((p, i) => (
                    <div key={p.id} className="flex items-center gap-2" data-testid={`package-row-${p.id}`}>
                      <Input className="h-11 rounded-xl font-semibold flex-1 min-w-0" value={p.name}
                        onChange={(e) => setFlat("packages", draft.packages.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                      <Input type="number" className="w-20 h-11 rounded-xl text-base font-semibold" value={p.price}
                        onChange={(e) => setFlat("packages", draft.packages.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} />
                      <button data-testid={`package-toggle-${p.id}`}
                        onClick={() => setFlat("packages", draft.packages.map((x, j) => j === i ? { ...x, active: !x.active } : x))}
                        className={`px-3 h-11 rounded-full border text-xs font-semibold transition-colors duration-200 shrink-0 ${p.active ? "border-emerald-500/50 text-emerald-500 bg-emerald-500/10" : "text-muted-foreground"}`}>
                        {p.active ? "On" : "Off"}
                      </button>
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-red-500 shrink-0" data-testid={`package-delete-${p.id}`}
                        onClick={() => setFlat("packages", draft.packages.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 pt-1">
                    <Input data-testid="package-name-input" className="h-11 rounded-xl flex-1 min-w-0" placeholder="e.g. Vegas Run"
                      value={newPkg.name} onChange={(e) => setNewPkg({ ...newPkg, name: e.target.value })} />
                    <Input data-testid="package-price-input" type="number" className="w-20 h-11 rounded-xl" placeholder="$"
                      value={newPkg.price} onChange={(e) => setNewPkg({ ...newPkg, price: e.target.value })} />
                    <Button data-testid="package-add-button" variant="outline" className="rounded-full h-11 shrink-0"
                      disabled={!newPkg.name.trim() || !newPkg.price}
                      onClick={() => { setFlat("packages", [...(draft.packages || []), { id: `pkg_${Date.now()}`, name: newPkg.name.trim(), price: Number(newPkg.price), active: true }]); setNewPkg({ name: "", price: "" }); }}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl border bg-card">
                <div className="px-6 py-4 border-b"><h2 className="font-heading font-bold text-base">Size Multipliers</h2></div>
                <div className="p-6 space-y-3">
                  {Object.keys(draft.size_mult).map((k) => (
                    <div key={k} className="flex items-center justify-between gap-3">
                      <span className="text-base">{SIZE_LABELS[k] || k}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-muted-foreground">×</span>
                        <Input data-testid={`pricing-size-${k}`} type="number" step="0.1" className="w-20 h-11 rounded-xl text-base font-semibold"
                          value={draft.size_mult[k]} onChange={(e) => setNested("size_mult", k, e.target.value)} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border bg-card">
                <div className="px-6 py-4 border-b"><h2 className="font-heading font-bold text-base">Fees & Extras</h2></div>
                <div className="p-6 space-y-3">
                  <FeeRow label="Special / heavy item" field="special_item_fee" fallback={75} />
                  <FeeRow label="Stairs (per flight)" field="stairs_fee" fallback={40} />
                  <FeeRow label="Last-minute priority" field="priority_fee" fallback={75} />
                  <FeeRow label="Extra delivery stop" field="extra_stop_fee" fallback={50} />
                  <div className="border-t pt-3 space-y-3">
                    <FeeRow label="Reservation deposit" field="deposit_percent" prefix="%" fallback={20} />
                    <FeeRow label="Referral discount" field="referral_percent" prefix="%" fallback={10} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border bg-card" data-testid="coverage-areas-card">
            <div className="px-6 py-4 border-b flex flex-wrap items-center gap-2">
              <MapPin className="w-4 h-4 text-[var(--cc-accent)]" />
              <h2 className="font-heading font-bold text-base">Coverage Areas</h2>
              <span className="text-xs text-muted-foreground">shown to customers so they know you cover their area — no pricing attached</span>
            </div>
            <div className="p-6">
              <div className="flex flex-wrap gap-2">
                {(draft.service_areas || []).map((a, i) => (
                  <span key={a + i} className="inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold bg-secondary/50" data-testid={`area-chip-${i}`}>
                    {a}
                    <button onClick={() => setFlat("service_areas", draft.service_areas.filter((_, j) => j !== i))} aria-label={`Remove ${a}`}>
                      <X className="w-3.5 h-3.5 text-muted-foreground hover:text-red-500" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-4 max-w-sm">
                <Input data-testid="area-add-input" className="h-11 rounded-xl" placeholder="Add an area — e.g. Carlsbad"
                  value={newArea} onChange={(e) => setNewArea(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && newArea.trim()) { setFlat("service_areas", [...(draft.service_areas || []), newArea.trim()]); setNewArea(""); } }} />
                <Button data-testid="area-add-button" variant="outline" className="rounded-full h-11 shrink-0" disabled={!newArea.trim()}
                  onClick={() => { setFlat("service_areas", [...(draft.service_areas || []), newArea.trim()]); setNewArea(""); }}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border bg-card" data-testid="promo-codes-card">
            <div className="px-6 py-4 border-b flex items-center gap-2">
              <Tag className="w-4 h-4 text-[var(--cc-accent)]" />
              <h2 className="font-heading font-bold text-base">Promo Codes</h2>
            </div>
            <div className="p-6 flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Code</Label>
                <Input data-testid="promo-code-input" value={newPromo.code} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value })} placeholder="SUMMER20" className="w-36 h-11 rounded-xl uppercase" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">% off</Label>
                <Input data-testid="promo-percent-input" type="number" value={newPromo.percent_off} onChange={(e) => setNewPromo({ ...newPromo, percent_off: e.target.value })} placeholder="20" className="w-20 h-11 rounded-xl" />
              </div>
              <div className="space-y-1.5 flex-1 min-w-[140px]">
                <Label className="text-xs">Label (optional)</Label>
                <Input data-testid="promo-label-input" value={newPromo.label} onChange={(e) => setNewPromo({ ...newPromo, label: e.target.value })} placeholder="Summer special" className="h-11 rounded-xl" />
              </div>
              <Button data-testid="promo-create-button" className="rounded-full h-11 bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold"
                onClick={addPromo} disabled={!newPromo.code.trim() || !newPromo.percent_off}>
                Create Code
              </Button>
            </div>
            <div className="divide-y border-t">
              {promos.map((p) => (
                <div key={p.id} className="px-6 py-3 flex items-center justify-between gap-3" data-testid={`promo-row-${p.code}`}>
                  <div className="min-w-0">
                    <p className="font-mono-cc text-sm font-bold">{p.code} <span className="text-emerald-500">— {p.percent_off}% off</span></p>
                    <p className="text-[11px] text-muted-foreground truncate">{p.label || "No label"} · created {fmtDate(p.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`promo-toggle-${p.code}`} onClick={() => togglePromo(p)}
                      className={`px-3 py-1 rounded-full border text-[11px] font-semibold transition-colors duration-200 ${p.active ? "border-emerald-500/50 text-emerald-500 bg-emerald-500/10" : "text-muted-foreground"}`}>
                      {p.active ? "Active" : "Paused"}
                    </button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" data-testid={`promo-delete-${p.code}`} onClick={() => deletePromo(p)} aria-label="Delete code">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
              {promos.length === 0 && <p className="px-6 py-5 text-xs text-muted-foreground">No promo codes yet. Every customer & crew member also has an automatic referral code ({draft.referral_percent}% off a friend's first booking).</p>}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border bg-card" data-testid="pending-payouts-card">
            <div className="px-6 py-4 border-b flex items-center gap-2">
              <Wallet className="w-4 h-4 text-[var(--cc-accent)]" />
              <h2 className="font-heading font-bold text-base">Helper Payouts</h2>
            </div>
            <div className="p-6">
              <p className="text-4xl font-heading font-extrabold" data-testid="pending-payout-total">{fmtMoney(payouts?.total || 0)}</p>
              <p className="text-xs text-muted-foreground mt-1">{payouts?.pending?.length || 0} pending earning(s) · {grouped.length} helper(s)</p>
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
                <CalendarClock className="w-3.5 h-3.5 text-[var(--cc-accent)]" />
                Auto-pays every Tue & Fri — next run {fmtDate(payouts?.next_payout)}
              </p>
              <Button data-testid="review-payouts-button" onClick={() => setReviewOpen(true)} disabled={!(payouts?.pending?.length)}
                className="w-full mt-4 rounded-full h-12 bg-emerald-500 text-black hover:bg-emerald-400 font-bold text-base">
                <Users className="w-4 h-4 mr-2" /> Review & Pay Now
              </Button>
            </div>
            <div className="divide-y border-t max-h-72 overflow-y-auto">
              {(payouts?.pending || []).map((e) => (
                <div key={e.id} className="px-6 py-3 flex items-center justify-between gap-3" data-testid={`pending-earning-${e.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{e.employee_name}</p>
                    <p className="text-[11px] text-muted-foreground font-mono-cc">{e.mission_number}</p>
                  </div>
                  <span className="font-mono-cc text-sm font-semibold">{fmtMoney(e.amount)}</span>
                </div>
              ))}
              {!(payouts?.pending?.length) && (
                <p className="px-6 py-6 text-xs text-muted-foreground text-center">No pending payouts. Earnings queue automatically when missions complete.</p>
              )}
            </div>
          </div>
          <div className="rounded-2xl border border-dashed p-6 text-xs text-muted-foreground leading-relaxed">
            <p className="font-semibold text-foreground mb-1">How it works</p>
            When a mission completes, each assigned helper earns either the job-board payout you set, or {draft.payout_percent}% of the job value split across the crew. You review every payout before money moves — or let the Tue/Fri auto-run handle it.
          </div>
        </div>
      </div>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent data-testid="payout-review-dialog" className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Review payout run</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Check every line before money moves. This pays all pending earnings.</p>
          <div className="divide-y rounded-xl border max-h-80 overflow-y-auto">
            {grouped.map((g) => (
              <div key={g.name} className="p-4" data-testid={`payout-review-${g.name.replace(/ /g, "-")}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{g.name}</p>
                  <p className="font-mono-cc font-bold text-emerald-500">{fmtMoney(g.total)}</p>
                </div>
                <div className="mt-1.5 space-y-0.5">
                  {g.records.map((r) => (
                    <p key={r.id} className="text-xs text-muted-foreground flex justify-between">
                      <span className="font-mono-cc">{r.mission_number}</span><span>{fmtMoney(r.amount)}</span>
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-emerald-500/40 bg-emerald-500/5 px-4 py-3">
            <p className="text-sm font-semibold">Total out the door</p>
            <p className="font-heading font-bold text-xl" data-testid="payout-review-total">{fmtMoney(payouts?.total || 0)}</p>
          </div>
          <Button data-testid="payout-confirm-button" onClick={runPayouts} disabled={running}
            className="w-full rounded-full h-12 bg-emerald-500 text-black hover:bg-emerald-400 font-bold">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm — Pay ${grouped.length} Helper${grouped.length !== 1 ? "s" : ""} Now`}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
